import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { BG_SUBTLE, BG_HOVER_LT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, BRAND_PRIMARY_TINT, ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_WARN, STATUS_BAD, SURFACE_WHITE, UNSAVED_AMBER, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_STAT_VALUE } from '../../theme';
import AISuggestButton from '../shared/AISuggestButton';
import ConfirmDialog from '../shared/ConfirmDialog';
import EvidenceSelector from '../shared/EvidenceSelector';

const FORMATS   = ['Session', 'Video', 'Infographic', 'Podcast', 'Quiz', 'Other'];
const LANGUAGES = ['es', 'en', 'both'];
const STATUSES  = ['Planned', 'Completed', 'Cancelled'];

const ACTIVITY_STATUS_COLORS = {
  Planned:   { bg: BG_MUTED, text: TEXT_MUTED },
  Completed: { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  Cancelled: { bg: ERROR_BG, text: ERROR_TEXT },
};

const AI_LABEL_REVIEW = { es: 'Revisar con IA ✨', en: 'Review with AI ✨' };


function genId() {
  return `aw-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function calcCoverage(attendees, totalStaff) {
  if (attendees == null || totalStaff == null || totalStaff <= 0) return null;
  return Math.round((Number(attendees) / Number(totalStaff)) * 1000) / 10;
}

function coverageRag(coverage, policy) {
  if (!policy || coverage == null) return null;
  if (coverage >= policy.ragGreen) return 'green';
  if (coverage >= policy.ragAmber) return 'amber';
  return 'red';
}

// ── MULTI-YEAR HELPERS ────────────────────────────────────────────────────────
// Source enum value of the Nonconformity schema for the Awareness gap source.
// Kept as a single module-level literal so both the multi-year NC filter and
// the red-band "Open NC" prefill reference one canonical string.
const NC_SOURCE_AWARENESS_GAP = 'Awareness gap';

function aggregateProgramme(programme, policy) {
  const acts = (programme && programme.activities) || [];
  const total = acts.length;
  const completed = acts.filter(a => a.status === 'Completed').length;
  const planned = acts.filter(a => a.status === 'Planned').length;
  const cancelled = acts.filter(a => a.status === 'Cancelled').length;
  const completedWithCov = acts.filter(a => a.status === 'Completed' && a.coveragePercent != null);
  const avgCoverage = completedWithCov.length > 0
    ? Math.round(completedWithCov.reduce((s, a) => s + a.coveragePercent, 0) / completedWithCov.length * 10) / 10
    : null;
  const ragCounts = completedWithCov.reduce((acc, act) => {
    const band = coverageRag(act.coveragePercent, policy);
    if (band === 'green') acc.green += 1;
    else if (band === 'amber') acc.amber += 1;
    else if (band === 'red') acc.red += 1;
    return acc;
  }, { green: 0, amber: 0, red: 0 });
  return { total, completed, planned, cancelled, avgCoverage, ragCounts };
}

function ncYearFor(nc) {
  if (typeof nc.date === 'string' && nc.date) {
    const d = new Date(nc.date);
    if (!isNaN(d.getTime())) return d.getFullYear();
  }
  if (typeof nc.createdAt === 'string' && nc.createdAt) {
    const d = new Date(nc.createdAt);
    if (!isNaN(d.getTime())) return d.getFullYear();
  }
  return null;
}

function groupAwarenessGapNcsByYear(ncs) {
  const groups = {};
  for (const nc of (ncs || [])) {
    if (nc.source !== NC_SOURCE_AWARENESS_GAP) continue;
    const y = ncYearFor(nc);
    if (y == null) continue;
    if (!groups[y]) groups[y] = { open: 0, closed: 0 };
    if (nc.status === 'Closed') groups[y].closed += 1;
    else groups[y].open += 1;
  }
  return groups;
}

function buildYearList(programmes, currentYear) {
  const years = new Set((programmes || []).map(p => p.year).filter(y => typeof y === 'number'));
  years.add(currentYear);
  const sorted = [...years].sort((a, b) => b - a);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);
    if (i < sorted.length - 1) {
      for (let g = sorted[i] - 1; g > sorted[i + 1]; g--) {
        result.push(g);
      }
    }
  }
  return result;
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:              'Programa de concienciación',
    prevYear:           '←',
    nextYear:           '→',
    save:               'Guardar',
    saving:             'Guardando…',
    saveOk:             '✓ Guardado',
    saveError:          'Error al guardar',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    loading:            'Cargando…',
    cntActivities:      'Actividades',
    cntCompleted:       'Completadas',
    cntCoverage:        'Cobertura media',
    kpiCoverageYtd:     'Cobertura (acumulada)',
    kpiTargetGap:       'Objetivo: {target}% · brecha {gap}',
    kpiStatus:          'Estado',
    kpiCompletedSubline:'{planned} planificadas · {cancelled} canceladas',
    ragOnTarget:        'En objetivo',
    ragAtRisk:          'En riesgo',
    ragBelowTarget:     'Bajo objetivo',
    editPolicyLink:     'Editar política en Ajustes',
    myLink:             'Vista multianual',
    myBackBtn:          'Volver a la vista anual',
    mySubtitle:         '· Comparativa multianual',
    myCaveatWithPolicy: 'Objetivo mostrado para todos los años: {target}% (política actual). Los cambios históricos de política no están versionados.',
    myCaveatNoPolicy:   'No hay política de cobertura definida. La columna de distribución RAG no aplica.',
    myColYear:          'Año',
    myColAvgCoverage:   'Cobertura media (acumulada)',
    myColActivities:    'Actividades',
    myColRagDistribution: 'Distribución RAG',
    myColNcs:           'NCs "Brecha de concienciación"',
    myCurrentYearSuffix:'actual',
    myEmptyState:       'Aún no hay programas registrados. Crea uno desde la vista anual.',
    myFooterHint:       'Haz clic en cualquier año con programa para abrir su vista detallada.',
    myActivitiesSubline:'{total} en total · {completed} / {planned} / {cancelled}',
    myNcsCell:          '{open} abiertas · {closed} cerradas',
    timelineHeader:        'Línea de tiempo',
    timelineActivityCount: '{count} actividades',
    legendPlanned:         'Planificada',
    legendOnTarget:        'En objetivo',
    legendAtRisk:          'En riesgo',
    legendBelowTarget:     'Bajo objetivo',
    legendCancelled:       'Cancelada',
    legendCompleted:       'Completada',
    ragBreakdownGreen:  'Verde',
    ragBreakdownAmber:  'Ámbar',
    fProgramNotes:      'Objetivos y contexto del programa',
    emptyTitle:         'No hay programa para este año',
    emptyHint:          'Genera un programa con IA o crea uno en blanco.',
    btnGenerate:        'Generar programa con IA ✨',
    btnEmpty:           'Crear programa vacío',
    btnCopyPrev:        'Copiar del año anterior',
    generating:         'Generando…',
    addActivity:        '+ Añadir actividad',
    colTitle:           'Título',
    colTopic:           'Tema',
    colFormat:          'Formato',
    colAudience:        'Público',
    colLanguage:        'Idioma',
    colPlannedDate:     'Fecha prevista',
    colActualDate:      'Fecha real',
    colStatus:          'Estado',
    colAttendees:       'Asistentes reales',
    colTotalStaff:      'Asistentes objetivo',
    colCoverage:        'Cobertura',
    colEvidenceId:      'ID evidencia',
    colNotes:           'Notas',
    fLinkedObjective:   'Objetivo vinculado',
    linkedObjectiveNone:'— Sin objetivo —',
    titlePlaceholder:   'p. ej. Concienciación phishing',
    topicPlaceholder:   'p. ej. Correo malicioso',
    audiencePlaceholder:'p. ej. Todo el personal',
    notesPlaceholder:   'Observaciones…',
    unnamedActivity:    'actividad sin título',
    deleteActivityConfirm: '¿Eliminar esta actividad?',
    deleteBtn:          'Eliminar',
    openNcBtn:               'Abrir no conformidad',
    ncDescriptionTemplate:   'La actividad de concienciación "{title}" alcanzó solo el {coverage}% de cobertura, por debajo del objetivo del {target}% (programa {year}).',
    btnReview:          'Revisar con IA ✨',
    reviewTitle:        'Sugerencias de mejora del programa',
    actionAdd:          'Añadir',
    actionModify:       'Modificar',
    applySelected:      'Aplicar seleccionadas',
    dismissSuggestions: 'Descartar',
    noActionsSelected:  'Selecciona al menos una acción',
    aiError:            'Error al conectar con la IA. Inténtalo de nuevo.',
    aiErrorOverloaded:  'El servidor de IA está ocupado. Inténtalo más tarde.',
    aiNoConsent:        'Consentimiento de procesamiento IA no concedido (ver Configuración).',
    noPastProgram:      'No hay programa registrado para este año.',
    formatLabels: {
      Session:     'Sesión',
      Video:       'Vídeo',
      Infographic: 'Infografía',
      Podcast:     'Podcast',
      Quiz:        'Cuestionario',
      Other:       'Otro',
    },
    languageLabels: {
      es:   'Español',
      en:   'Inglés',
      both: 'Ambos',
    },
    statusLabels: {
      Planned:   'Planificado',
      Completed: 'Completado',
      Cancelled: 'Cancelado',
    },
  },
  en: {
    title:              'Awareness program',
    prevYear:           '←',
    nextYear:           '→',
    save:               'Save',
    saving:             'Saving…',
    saveOk:             '✓ Saved',
    saveError:          'Save error',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    loading:            'Loading…',
    cntActivities:      'Activities',
    cntCompleted:       'Completed',
    cntCoverage:        'Avg. coverage',
    kpiCoverageYtd:     'Coverage (year to date)',
    kpiTargetGap:       'Target: {target}% · gap {gap}',
    kpiStatus:          'Status',
    kpiCompletedSubline:'{planned} planned · {cancelled} cancelled',
    ragOnTarget:        'On target',
    ragAtRisk:          'At risk',
    ragBelowTarget:     'Below target',
    editPolicyLink:     'Edit policy in Settings',
    myLink:             'Multi-year view',
    myBackBtn:          'Back to year view',
    mySubtitle:         '· Multi-year comparison',
    myCaveatWithPolicy: 'Target shown for every year: {target}% (current policy). Historical policy changes are not versioned.',
    myCaveatNoPolicy:   'No coverage policy defined. The RAG distribution column does not apply.',
    myColYear:          'Year',
    myColAvgCoverage:   'Avg coverage (YTD)',
    myColActivities:    'Activities',
    myColRagDistribution: 'RAG of completed',
    myColNcs:           'NCs "Awareness gap"',
    myCurrentYearSuffix:'current',
    myEmptyState:       'No programs recorded yet. Create one from the year view.',
    myFooterHint:       'Click any year with a program to open its detailed view.',
    myActivitiesSubline:'{total} total · {completed} / {planned} / {cancelled}',
    myNcsCell:          '{open} open · {closed} closed',
    timelineHeader:        'Timeline',
    timelineActivityCount: '{count} activities',
    legendPlanned:         'Planned',
    legendOnTarget:        'On target',
    legendAtRisk:          'At risk',
    legendBelowTarget:     'Below target',
    legendCancelled:       'Cancelled',
    legendCompleted:       'Completed',
    ragBreakdownGreen:  'Green',
    ragBreakdownAmber:  'Amber',
    fProgramNotes:      'Program goals and context',
    emptyTitle:         'No program for this year',
    emptyHint:          'Generate a program with AI or create an empty one.',
    btnGenerate:        'Generate program with AI ✨',
    btnEmpty:           'Create empty program',
    btnCopyPrev:        'Copy from previous year',
    generating:         'Generating…',
    addActivity:        '+ Add activity',
    colTitle:           'Title',
    colTopic:           'Topic',
    colFormat:          'Format',
    colAudience:        'Audience',
    colLanguage:        'Language',
    colPlannedDate:     'Planned date',
    colActualDate:      'Actual date',
    colStatus:          'Status',
    colAttendees:       'Actual attendees',
    colTotalStaff:      'Target attendees',
    colCoverage:        'Coverage',
    colEvidenceId:      'Evidence ID',
    colNotes:           'Notes',
    fLinkedObjective:   'Linked objective',
    linkedObjectiveNone:'— No objective —',
    titlePlaceholder:   'e.g. Phishing awareness',
    topicPlaceholder:   'e.g. Malicious email',
    audiencePlaceholder:'e.g. All staff',
    notesPlaceholder:   'Observations…',
    unnamedActivity:    'unnamed activity',
    deleteActivityConfirm: 'Delete this activity?',
    deleteBtn:          'Delete',
    openNcBtn:               'Open nonconformity',
    ncDescriptionTemplate:   'The awareness activity "{title}" reached only {coverage}% coverage, below the {target}% target ({year} program).',
    btnReview:          'Review with AI ✨',
    reviewTitle:        'Program improvement suggestions',
    actionAdd:          'Add',
    actionModify:       'Modify',
    applySelected:      'Apply selected',
    dismissSuggestions: 'Dismiss',
    noActionsSelected:  'Select at least one action',
    aiError:            'Error connecting to AI. Please try again.',
    aiErrorOverloaded:  'AI server is busy. Please try again later.',
    aiNoConsent:        'AI processing consent not granted (see Settings).',
    noPastProgram:      'No program recorded for this year.',
    formatLabels: {
      Session:     'Session',
      Video:       'Video',
      Infographic: 'Infographic',
      Podcast:     'Podcast',
      Quiz:        'Quiz',
      Other:       'Other',
    },
    languageLabels: {
      es:   'Spanish',
      en:   'English',
      both: 'Both',
    },
    statusLabels: {
      Planned:   'Planned',
      Completed: 'Completed',
      Cancelled: 'Cancelled',
    },
  },
};

// ── KPI CARD ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, valueColor, inlineBadge, subline }) {
  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
      padding: '0.9rem 1.25rem',
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '0.5rem',
      }}>
        <span style={{
          ...FONT_STAT_VALUE, color: valueColor,
        }}>
          {value}
        </span>
        {inlineBadge && (
          <span style={{
            ...FONT_TINY,
            background: inlineBadge.bg, color: inlineBadge.text,
            borderRadius: 4, padding: '0.1rem 0.5rem', fontWeight: 500,
          }}>
            {inlineBadge.label}
          </span>
        )}
      </div>
      {subline && (
        <div style={{
          ...FONT_SMALL,
          color: TEXT_MUTED, marginTop: '0.35rem', lineHeight: 1.4,
        }}>
          {subline}
        </div>
      )}
    </div>
  );
}

// ── MULTI-YEAR BODY ───────────────────────────────────────────────────────────
function MultiYearBody({ programmes, ncs, policy, t, currentYear, loading, onRowClick }) {
  const containerSt = {
    background: BG_SURFACE,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 10,
    overflow: 'hidden',
  };
  const headerStripSt = {
    ...FONT_SMALL,
    color: TEXT_MUTED,
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    background: BG_SUBTLE,
  };
  const caveat = policy
    ? t.myCaveatWithPolicy.replace('{target}', String(policy.target))
    : t.myCaveatNoPolicy;

  if (loading) {
    return (
      <div style={containerSt}>
        <div style={headerStripSt}>{caveat}</div>
        <div style={{ ...FONT_LABEL, color: TEXT_MUTED, padding: '2rem', textAlign: 'center' }}>
          {t.loading}
        </div>
      </div>
    );
  }

  if (!programmes || programmes.length === 0) {
    return (
      <div style={containerSt}>
        <div style={headerStripSt}>{caveat}</div>
        <div style={{ ...FONT_LABEL, color: TEXT_MUTED, padding: '2.25rem', textAlign: 'center' }}>
          {t.myEmptyState}
        </div>
      </div>
    );
  }

  const programmesByYear = new Map(programmes.map(p => [p.year, p]));
  const yearList = buildYearList(programmes, currentYear);
  const ncsByYear = groupAwarenessGapNcsByYear(ncs);

  const thSt = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    textAlign: 'left',
    fontWeight: 500,
    padding: '0.6rem 0.85rem',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    background: BG_SUBTLE,
    whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_BODY,
    color: TEXT_DEFAULT,
    padding: '0.85rem',
    verticalAlign: 'top',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
  };

  const ragPill = (band) => {
    const pair = band === 'green' ? STATUS_GOOD
      : band === 'amber' ? STATUS_WARN
      : STATUS_BAD;
    const label = band === 'green' ? t.ragOnTarget
      : band === 'amber' ? t.ragAtRisk
      : t.ragBelowTarget;
    return (
      <span style={{
        ...FONT_TINY,
        background: pair.bg, color: pair.text,
        borderRadius: 4, padding: '0.1rem 0.5rem',
        marginLeft: '0.5rem',
        fontWeight: 500,
        display: 'inline-block',
      }}>
        {label}
      </span>
    );
  };

  const stackedBar = (agg) => {
    const segH = 8;
    const radius = 4;
    if (agg.total === 0) {
      return (
        <div style={{
          width: '100%', height: `${segH}px`, borderRadius: radius,
          background: BORDER_DEFAULT,
        }} />
      );
    }
    const cPct = (agg.completed / agg.total) * 100;
    const pPct = (agg.planned   / agg.total) * 100;
    const xPct = (agg.cancelled / agg.total) * 100;
    return (
      <div style={{
        display: 'flex', width: '100%', height: `${segH}px`,
        borderRadius: radius, overflow: 'hidden',
      }}>
        {cPct > 0 && <div style={{ width: `${cPct}%`, background: STATUS_GOOD.text }} />}
        {pPct > 0 && <div style={{ width: `${pPct}%`, background: STATUS_WARN.text }} />}
        {xPct > 0 && <div style={{ width: `${xPct}%`, background: TEXT_DISABLED }} />}
      </div>
    );
  };

  const dot = (color) => (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: color, flexShrink: 0,
    }} />
  );

  return (
    <div style={containerSt}>
      <div style={headerStripSt}>{caveat}</div>
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '110px' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '28%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={thSt}>{t.myColYear}</th>
            <th style={thSt}>{t.myColAvgCoverage}</th>
            <th style={thSt}>{t.myColActivities}</th>
            <th style={thSt}>{t.myColRagDistribution}</th>
            <th style={thSt}>{t.myColNcs}</th>
          </tr>
        </thead>
        <tbody>
          {yearList.map((y, idx) => {
            const programme = programmesByYear.get(y) || null;
            const isGap = !programme;
            const isCurrent = y === currentYear;
            const rowTintSt = isCurrent ? { background: BRAND_PRIMARY_TINT } : {};
            const isLast = idx === yearList.length - 1;
            const cellBorderSt = isLast ? { borderBottom: 'none' } : {};

            const yearCell = (
              <td style={{
                ...tdSt, ...cellBorderSt,
                color: isGap ? TEXT_MUTED : TEXT_HEADING,
                whiteSpace: 'nowrap',
              }}>
                <span style={{ ...FONT_BODY_STRONG, color: isGap ? TEXT_MUTED : TEXT_HEADING }}>
                  {y}
                </span>
                {isCurrent && (
                  <div style={{ ...FONT_SMALL, color: TEXT_MUTED, marginTop: '0.15rem' }}>
                    {t.myCurrentYearSuffix}
                  </div>
                )}
              </td>
            );

            if (isGap) {
              return (
                <tr key={y} style={rowTintSt}>
                  {yearCell}
                  <td colSpan={4} style={{
                    ...tdSt, ...cellBorderSt,
                    ...FONT_SMALL,
                    fontStyle: 'italic',
                    color: TEXT_MUTED,
                  }}>
                    {t.noPastProgram}
                  </td>
                </tr>
              );
            }

            const agg = aggregateProgramme(programme, policy);
            const band = coverageRag(agg.avgCoverage, policy);
            const ncInfo = ncsByYear[y] || { open: 0, closed: 0 };

            const avgCovValue = agg.avgCoverage != null ? `${agg.avgCoverage}%` : '—';

            const subline = t.myActivitiesSubline
              .replace('{total}',     String(agg.total))
              .replace('{completed}', String(agg.completed))
              .replace('{planned}',   String(agg.planned))
              .replace('{cancelled}', String(agg.cancelled));

            return (
              <tr
                key={y}
                onClick={() => onRowClick(y)}
                onMouseEnter={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = BG_HOVER_LT;
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = '';
                }}
                style={{
                  ...rowTintSt,
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
              >
                {yearCell}
                <td style={{ ...tdSt, ...cellBorderSt }}>
                  <span style={{ ...FONT_BODY_STRONG, color: TEXT_HEADING }}>
                    {avgCovValue}
                  </span>
                  {policy && agg.avgCoverage != null && band && ragPill(band)}
                </td>
                <td style={{ ...tdSt, ...cellBorderSt }}>
                  {stackedBar(agg)}
                  <div style={{ ...FONT_SMALL, color: TEXT_MUTED, marginTop: '0.4rem' }}>
                    {subline}
                  </div>
                </td>
                <td style={{ ...tdSt, ...cellBorderSt }}>
                  {policy ? (
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {dot(BRAND_PRIMARY)} {agg.ragCounts.green}
                      </span>
                      <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {dot(UNSAVED_AMBER)} {agg.ragCounts.amber}
                      </span>
                      <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {dot(FIELD_ERROR_BORDER)}  {agg.ragCounts.red}
                      </span>
                    </div>
                  ) : (
                    <span style={{ ...FONT_BODY, color: TEXT_MUTED }}>—</span>
                  )}
                </td>
                <td style={{ ...tdSt, ...cellBorderSt }}>
                  {(() => {
                    const tpl = t.myNcsCell;
                    const oTag = '{open}';
                    const cTag = '{closed}';
                    const iO = tpl.indexOf(oTag);
                    const iC = tpl.indexOf(cTag);
                    const before  = tpl.slice(0, iO);
                    const between = tpl.slice(iO + oTag.length, iC);
                    const after   = tpl.slice(iC + cTag.length);
                    return (
                      <>
                        {before && <span style={{ ...FONT_BODY, color: TEXT_MUTED }}>{before}</span>}
                        <span style={{ ...FONT_BODY_STRONG, color: TEXT_DEFAULT }}>{ncInfo.open}</span>
                        <span style={{ ...FONT_BODY, color: TEXT_MUTED }}>{between}</span>
                        <span style={{ ...FONT_BODY, color: TEXT_MUTED }}>{ncInfo.closed}</span>
                        <span style={{ ...FONT_BODY, color: TEXT_MUTED }}>{after}</span>
                      </>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_MUTED,
        padding: '0.75rem 1rem',
        borderTop: `1px solid ${BORDER_DEFAULT}`,
        background: BG_SUBTLE,
      }}>
        {t.myFooterHint}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function AwarenessView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t           = T[lang] || T.en;
  const noConsent   = org.aiProcessingConsent === false;
  const currentYear = new Date().getFullYear();

  // program: null = loading, false = not found (show empty-state panel), object = loaded/created
  const [program,          setProgram]          = useState(null);
  const [year,             setYear]             = useState(new Date().getFullYear());
  const [saveState,        setSaveState]        = useState('idle');
  const [dirty,            setDirty]            = useState(false);
  const [confirmState,     setConfirmState]     = useState(null);
  const [pendingNotes,     setPendingNotes]     = useState('');
  const [prevYearExists,   setPrevYearExists]   = useState(false);
  const [copyingPrev,      setCopyingPrev]      = useState(false);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [aiGenerateState,  setAiGenerateState]  = useState({ loading: false, error: null });
  const [aiReviewState,    setAiReviewState]    = useState({ loading: false, error: null });
  const [aiReviewActions,  setAiReviewActions]  = useState(null);
  const [checkedActions,   setCheckedActions]   = useState([]);
  const [objectives,       setObjectives]       = useState([]);
  const [mode,             setMode]             = useState('year');
  const [multiYearCache,   setMultiYearCache]   = useState(null);
  const [multiYearLoading, setMultiYearLoading] = useState(false);

  const savedProgramRef = useRef(null);

  // ── Load program for year ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setProgram(null);
    setDirty(false);
    setAiReviewActions(null);
    setCheckedActions([]);
    setExpandedActivity(null);
    setPrevYearExists(false);
    setPendingNotes('');
    (async () => {
      try {
        const data = await readOrgFile(org.id, `awareness/program-${year}.json`);
        if (!cancelled) {
          setProgram(data);
          savedProgramRef.current = data;
        }
      } catch {
        if (!cancelled) {
          setProgram(false);
          savedProgramRef.current = null;
          try {
            await readOrgFile(org.id, `awareness/program-${year - 1}.json`);
            if (!cancelled) setPrevYearExists(true);
          } catch {
            // no previous year
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [org.id, year]);

  // ── Load objectives for linked-objective selector ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listOrgCollection(org.id, 'objectives');
        if (!cancelled) setObjectives(list || []);
      } catch {
        if (!cancelled) setObjectives([]);
      }
    })();
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Reset mode and multi-year cache on org change ─────────────────────────
  useEffect(() => {
    setMode('year');
    setMultiYearCache(null);
  }, [org.id]);

  // ── Lazy fetch multi-year data on first transition into multi-year mode ──
  useEffect(() => {
    if (mode !== 'multiYear') return;
    if (multiYearCache !== null) return;
    let cancelled = false;
    setMultiYearLoading(true);
    (async () => {
      try {
        const [programmes, ncList] = await Promise.all([
          listOrgCollection(org.id, 'awareness'),
          listOrgCollection(org.id, 'nonconformities'),
        ]);
        if (!cancelled) {
          setMultiYearCache({
            programmes: programmes || [],
            nonconformities: ncList || [],
          });
        }
      } catch {
        if (!cancelled) {
          setMultiYearCache({ programmes: [], nonconformities: [] });
        }
      } finally {
        if (!cancelled) setMultiYearLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, multiYearCache, org.id]);

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const doSave = async () => {
    if (!program || saveState === 'saving') return false;
    setSaveState('saving');
    try {
      const changedBy = org.consultantPersonId || 'system';
      const updated   = { ...program, updatedAt: new Date().toISOString() };
      updated.changeLog = appendChangeLog(savedProgramRef.current, updated, changedBy);
      await writeOrgFile(org.id, `awareness/program-${year}.json`, updated);
      savedProgramRef.current = { ...updated };
      setProgram(updated);
      setDirty(false);
      setMultiYearCache(null);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Year navigation with dirty guard ─────────────────────────────────────
  const handleYearNav = (delta) => {
    if (dirty) {
      setConfirmState({
        message:      t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm:    () => { setConfirmState(null); setYear(y => y + delta); },
        secondaryAction: {
          label:   t.saveAndLeave,
          onClick: async () => {
            setConfirmState(null);
            const ok = await doSave();
            if (ok) setYear(y => y + delta);
          },
        },
      });
      return;
    }
    setYear(y => y + delta);
  };

  // ── Mode switch (year ↔ multiYear) ────────────────────────────────────────
  const handleSwitchToMultiYear = () => {
    if (dirty) {
      setConfirmState({
        message:      t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm:    () => { setConfirmState(null); setMode('multiYear'); },
        secondaryAction: {
          label:   t.saveAndLeave,
          onClick: async () => {
            setConfirmState(null);
            const ok = await doSave();
            if (ok) setMode('multiYear');
          },
        },
      });
      return;
    }
    setMode('multiYear');
  };

  const handleSwitchToYear = () => {
    setMode('year');
  };

  const handleMultiYearRowClick = (rowYear) => {
    setYear(rowYear);
    setMode('year');
  };

  // ── Create empty programme ────────────────────────────────────────────────
  const handleCreateEmpty = () => {
    const blank = {
      id:           `awareness-${org.id}-${year}`,
      orgId:        org.id,
      year,
      programNotes: pendingNotes,
      basedOnYear:  null,
      activities:   [],
      updatedAt:    new Date().toISOString(),
      changeLog:    [],
    };
    savedProgramRef.current = { ...blank };
    setProgram(blank);
    setDirty(true);
  };

  // ── Copy from previous year ───────────────────────────────────────────────
  const handleCopyPrev = async () => {
    setCopyingPrev(true);
    try {
      const prev = await readOrgFile(org.id, `awareness/program-${year - 1}.json`);
      const copiedActivities = (prev.activities || []).map(a => ({
        ...a,
        id:              genId(),
        plannedDate:     '',
        actualDate:      null,
        status:          'Planned',
        attendees:       null,
        coveragePercent: null,
        linkedObjectiveId: null,
      }));
      const newProg = {
        id:           `awareness-${org.id}-${year}`,
        orgId:        org.id,
        year,
        programNotes: pendingNotes || prev.programNotes || '',
        basedOnYear:  year - 1,
        activities:   copiedActivities,
        updatedAt:    new Date().toISOString(),
        changeLog:    [],
      };
      savedProgramRef.current = { ...newProg };
      setProgram(newProg);
      setDirty(true);
    } catch {
      // silently ignore — button only shown when prev year exists
    } finally {
      setCopyingPrev(false);
    }
  };

  // ── AI: generate programme ────────────────────────────────────────────────
  const handleAIGenerate = async () => {
    if (noConsent) return;
    setAiGenerateState({ loading: true, error: null });
    const promptParts = [
      'You are an ISO 27001:2022 Lead Implementer assistant.',
      'Generate an information security awareness program for the organization described below.',
      `Sector: ${org.sector || 'Not specified'}`,
      `Organization size (employees): ${org.size || 'Not specified'}`,
      `Technologies in use: ${(org.technologies || []).join(', ') || 'Not specified'}`,
      `Organization language: ${org.language || 'Not specified'}`,
    ];
    if (pendingNotes) promptParts.push(`Program goals and context: ${pendingNotes}`);
    promptParts.push(
      'Return ONLY a valid JSON array of activity objects. Generate as many as needed based on the context provided — typically between 4 and 20. No markdown, no preamble.',
      'Each object must have exactly these fields:',
      '  title (string), topic (string), format (one of "Session","Video","Infographic","Podcast","Quiz","Other"),',
      '  targetAudience (string), language (one of "es","en","both"),',
      '  plannedDate (empty string ""), actualDate (null),',
      '  status ("Planned"), attendees (null), totalStaff (0), coveragePercent (null),',
      '  evidenceId (null), notes (empty string ""), linkedObjectiveId (null).',
      'Example: [{"title":"Phishing awareness","topic":"Malicious email","format":"Session","targetAudience":"All staff","language":"es","plannedDate":"","actualDate":null,"status":"Planned","attendees":null,"totalStaff":0,"coveragePercent":null,"evidenceId":null,"notes":"","linkedObjectiveId":null}]'
    );
    const prompt = promptParts.join('\n');

    const result = await callWorker({ prompt, maxTokens: 4000 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiGenerateState({ loading: false, error: busy ? t.aiErrorOverloaded : t.aiError });
      return;
    }
    const stripped = result.text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
    let parsed;
    try { parsed = JSON.parse(stripped); } catch {
      setAiGenerateState({ loading: false, error: t.aiError });
      return;
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      const activities = parsed.map(a => ({
        id:              genId(),
        title:           typeof a.title === 'string' ? a.title : '',
        topic:           typeof a.topic === 'string' ? a.topic : '',
        format:          FORMATS.includes(a.format) ? a.format : 'Session',
        targetAudience:  typeof a.targetAudience === 'string' ? a.targetAudience : '',
        language:        LANGUAGES.includes(a.language) ? a.language : 'es',
        plannedDate:     '',
        actualDate:      null,
        status:          'Planned',
        attendees:       null,
        totalStaff:      0,
        coveragePercent: null,
        evidenceId:      null,
        notes:           '',
        linkedObjectiveId: null,
      }));
      const newProg = {
        id:           `awareness-${org.id}-${year}`,
        orgId:        org.id,
        year,
        programNotes: pendingNotes,
        basedOnYear:  null,
        activities,
        updatedAt:    new Date().toISOString(),
        changeLog:    [],
      };
      savedProgramRef.current = { ...newProg };
      setProgram(newProg);
      setDirty(true);
    }
    setAiGenerateState({ loading: false, error: null });
  };

  // ── AI: review programme ──────────────────────────────────────────────────
  const handleAIReview = async () => {
    if (noConsent || !program || !Array.isArray(program.activities)) return;
    setAiReviewState({ loading: true, error: null });
    setAiReviewActions(null);
    setCheckedActions([]);
    const activitiesContext = (program.activities || []).map((a, i) =>
      `${i + 1}. [id: "${a.id}"] "${a.title}" — topic: ${a.topic}, format: ${a.format}, status: ${a.status}`
    ).join('\n');
    const prompt = [
      'You are an ISO 27001:2022 Lead Implementer assistant.',
      'Review the following information security awareness program and suggest improvements.',
      `Sector: ${org.sector || 'Not specified'}`,
      `Organization size (employees): ${org.size || 'Not specified'}`,
      `Technologies in use: ${(org.technologies || []).join(', ') || 'Not specified'}`,
      'Current activities:',
      activitiesContext,
      'Return ONLY a valid JSON array of 2 to 5 action objects. No preamble, no markdown fences.',
      'Each action must have:',
      '  "type": "add" or "modify"',
      '  "reason": a short explanation under 80 characters',
      '  "activity": an object with these fields: title, topic, format (one of "Session","Video","Infographic","Podcast","Quiz","Other"), targetAudience, language (one of "es","en","both"), plannedDate (empty string), status ("Planned"), attendees (null), totalStaff (0), coveragePercent (null), evidenceId (null), notes (empty string)',
      'For "modify" type, also include "id" at the top level with the ID of the existing activity to update.',
      'Example: [{"type":"add","reason":"Missing social engineering training","activity":{"title":"Social engineering","topic":"Manipulation tactics","format":"Session","targetAudience":"All staff","language":"en","plannedDate":"","status":"Planned","attendees":null,"totalStaff":0,"coveragePercent":null,"evidenceId":null,"notes":""}},{"type":"modify","id":"aw-123-abc","reason":"Add specific target audience","activity":{"title":"Phishing awareness","topic":"Malicious email","format":"Session","targetAudience":"IT and Finance teams","language":"en","plannedDate":"","status":"Planned","attendees":null,"totalStaff":0,"coveragePercent":null,"evidenceId":null,"notes":""}}]',
    ].join('\n');

    const result = await callWorker({ prompt, maxTokens: 1000 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiReviewState({ loading: false, error: busy ? t.aiErrorOverloaded : t.aiError });
      return;
    }
    const stripped = result.text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
    let parsed;
    try { parsed = JSON.parse(stripped); } catch {
      setAiReviewState({ loading: false, error: t.aiError });
      return;
    }
    if (Array.isArray(parsed)) {
      const valid = parsed.filter(a =>
        (a.type === 'add' || a.type === 'modify') &&
        a.activity &&
        typeof a.reason === 'string'
      );
      if (valid.length > 0) {
        setAiReviewActions(valid);
        setCheckedActions(new Array(valid.length).fill(true));
      }
    }
    setAiReviewState({ loading: false, error: null });
  };

  // ── Apply AI review actions ───────────────────────────────────────────────
  const handleApplyReview = () => {
    if (!aiReviewActions) return;
    const toApply = aiReviewActions.filter((_, i) => checkedActions[i]);
    if (toApply.length === 0) return;
    let newActivities = [...(program.activities || [])];
    for (const action of toApply) {
      const act = action.activity || {};
      if (action.type === 'add') {
        const newAct = {
          id:              genId(),
          title:           typeof act.title === 'string' ? act.title : '',
          topic:           typeof act.topic === 'string' ? act.topic : '',
          format:          FORMATS.includes(act.format) ? act.format : 'Session',
          targetAudience:  typeof act.targetAudience === 'string' ? act.targetAudience : '',
          language:        LANGUAGES.includes(act.language) ? act.language : 'es',
          plannedDate:     '',
          actualDate:      null,
          status:          'Planned',
          attendees:       null,
          totalStaff:      0,
          coveragePercent: null,
          evidenceId:      null,
          notes:           typeof act.notes === 'string' ? act.notes : '',
          linkedObjectiveId: null,
        };
        newActivities.push(newAct);
      } else if (action.type === 'modify' && action.id) {
        newActivities = newActivities.map(a => {
          if (a.id !== action.id) return a;
          const merged = {
            ...a,
            title:          typeof act.title === 'string' ? act.title : a.title,
            topic:          typeof act.topic === 'string' ? act.topic : a.topic,
            format:         FORMATS.includes(act.format) ? act.format : a.format,
            targetAudience: typeof act.targetAudience === 'string' ? act.targetAudience : a.targetAudience,
            language:       LANGUAGES.includes(act.language) ? act.language : a.language,
            plannedDate:    typeof act.plannedDate === 'string' ? act.plannedDate : a.plannedDate,
            status:         STATUSES.includes(act.status) ? act.status : a.status,
            linkedObjectiveId: a.linkedObjectiveId,
          };
          return { ...merged, coveragePercent: calcCoverage(merged.attendees, merged.totalStaff) };
        });
      }
    }
    setProgram(prev => ({ ...prev, activities: newActivities }));
    setDirty(true);
    setAiReviewActions(null);
    setCheckedActions([]);
  };

  // ── Activity operations ───────────────────────────────────────────────────
  const handleToggleActivity = (actId) => {
    setExpandedActivity(prev => prev === actId ? null : actId);
  };

  const handleTimelineRowClick = (actId) => {
    if (expandedActivity !== actId) setExpandedActivity(actId);
    setTimeout(() => {
      document.getElementById(`awareness-activity-${actId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleAddActivity = () => {
    const newId = genId();
    const newActivity = {
      id:              newId,
      title:           '',
      topic:           '',
      format:          'Session',
      targetAudience:  '',
      language:        'es',
      plannedDate:     '',
      actualDate:      null,
      status:          'Planned',
      attendees:       null,
      totalStaff:      0,
      coveragePercent: null,
      evidenceId:      null,
      notes:           '',
      linkedObjectiveId: null,
    };
    setDirty(true);
    setProgram(prev => ({ ...prev, activities: [...prev.activities, newActivity] }));
    setExpandedActivity(newId);
  };

  const setActivityField = (actId, field, value) => {
    setDirty(true);
    setProgram(prev => ({
      ...prev,
      activities: prev.activities.map(a => {
        if (a.id !== actId) return a;
        const updated = { ...a, [field]: value };
        if (field === 'attendees' || field === 'totalStaff') {
          const att = field === 'attendees' ? value : a.attendees;
          const tot = field === 'totalStaff' ? value : a.totalStaff;
          updated.coveragePercent = calcCoverage(att, tot);
        }
        return updated;
      }),
    }));
  };

  const handleDeleteActivity = (actId) => {
    setConfirmState({
      message:      t.deleteActivityConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        setDirty(true);
        setProgram(prev => ({ ...prev, activities: prev.activities.filter(a => a.id !== actId) }));
        setExpandedActivity(prev => prev === actId ? null : prev);
      },
    });
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (program === null && mode === 'year') {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const activities       = program ? (program.activities || []) : [];
  const totalActivities  = activities.length;
  const completedCount   = activities.filter(a => a.status === 'Completed').length;
  const completedWithCov = activities.filter(a => a.status === 'Completed' && a.coveragePercent != null);
  const avgCoverage      = completedWithCov.length > 0
    ? Math.round(completedWithCov.reduce((s, a) => s + a.coveragePercent, 0) / completedWithCov.length * 10) / 10
    : null;
  const policy           = org.awarenessCoveragePolicy || null;
  const programmeRag     = coverageRag(avgCoverage, policy);
  const ragCounts        = completedWithCov.reduce((acc, act) => {
    const band = coverageRag(act.coveragePercent, policy);
    if (band === 'green') acc.green += 1;
    else if (band === 'amber') acc.amber += 1;
    else if (band === 'red') acc.red += 1;
    return acc;
  }, { green: 0, amber: 0, red: 0 });
  const plannedCount     = activities.filter(a => a.status === 'Planned').length;
  const cancelledCount   = activities.filter(a => a.status === 'Cancelled').length;
  const coverageGap      = (policy && avgCoverage != null)
    ? Math.round(avgCoverage - policy.target)
    : null;
  const timelineActivities = (() => {
    const allowed = new Set(['Planned', 'Completed', 'Cancelled']);
    const eff = (a) => {
      if (typeof a.plannedDate === 'string' && a.plannedDate) {
        const p = Date.parse(a.plannedDate);
        if (!isNaN(p)) return p;
      }
      if (a.actualDate) {
        const r = Date.parse(a.actualDate);
        if (!isNaN(r)) return r;
      }
      return Number.MAX_SAFE_INTEGER;
    };
    return activities
      .filter(a => allowed.has(a.status))
      .slice()
      .sort((a, b) => eff(a) - eff(b));
  })();
  const hasActivities    = !!program && activities.length > 0;
  const saveLabel = { idle: t.save, saving: t.saving, ok: t.saveOk, error: t.save }[saveState];
  const saveBg    = { idle: BRAND_PRIMARY, saving: TEXT_MUTED, ok: BRAND_PRIMARY_DARK, error: BRAND_PRIMARY }[saveState];
  const isSaving  = saveState === 'saving';
  const noneChecked      = !checkedActions.some(Boolean);
  const isCreatableYear  = year === currentYear || year === currentYear + 1;
  const isViewOnly       = !isCreatableYear;

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputSt = {
    ...FONT_BODY,
    width: '100%',
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.45rem 0.65rem',
    background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', boxSizing: 'border-box',
  };
  const labelSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: '0.35rem',
  };
  const fieldSt = { display: 'flex', flexDirection: 'column' };
  const cellInputSt = {
    ...FONT_LABEL,
    width: '100%',
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.4rem 0.6rem',
    background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn}
        cancelLabel={t.formCancel}
        secondaryAction={confirmState?.secondaryAction}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 style={{
              ...FONT_PAGE_TITLE,
              color: TEXT_HEADING, margin: 0,
            }}>
              {t.title}
            </h1>
            {mode === 'year' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 8,
                padding: '0.2rem 0.5rem', background: BG_SURFACE,
              }}>
                <button
                  onClick={() => handleYearNav(-1)}
                  style={{
                    ...FONT_LABEL,
                    background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED,
                    padding: '0 0.1rem', lineHeight: 1,
                  }}
                >
                  {t.prevYear}
                </button>
                <span style={{
                  ...FONT_LABEL, color: TEXT_HEADING, fontWeight: 600,
                  minWidth: '3rem', textAlign: 'center', letterSpacing: '0.04em',
                }}>
                  {year}
                </span>
                <button
                  onClick={() => handleYearNav(1)}
                  style={{
                    ...FONT_LABEL,
                    background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED,
                    padding: '0 0.1rem', lineHeight: 1,
                  }}
                >
                  {t.nextYear}
                </button>
              </div>
            )}
            {mode === 'year' && (
              <button
                type="button"
                onClick={handleSwitchToMultiYear}
                onMouseEnter={(e) => { e.currentTarget.style.background = BG_HOVER_LT; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = BG_SURFACE; }}
                style={{
                  ...FONT_LABEL,
                  color: TEXT_HEADING,
                  border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 8,
                  padding: '0.2rem 0.7rem',
                  background: BG_SURFACE,
                  cursor: 'pointer',
                }}
              >
                {t.myLink}
              </button>
            )}
            {mode === 'multiYear' && (
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
                {t.mySubtitle}
              </span>
            )}
          </div>
        </div>

        {/* Right group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', alignSelf: 'flex-end', flexWrap: 'wrap' }}>
          {mode === 'year' && typeof onNavigate === 'function' && (
            <button
              type="button"
              onClick={() => onNavigate('settings', { section: 'awareness' })}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: TEXT_MUTED, textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {t.editPolicyLink}
            </button>
          )}
          {mode === 'year' && typeof onNavigate === 'function' && hasActivities && !isViewOnly && (
            <span style={{ color: TEXT_MUTED }}>·</span>
          )}
          {mode === 'year' && hasActivities && !isViewOnly && (
            <AISuggestButton
              onClick={handleAIReview}
              loading={aiReviewState.loading}
              disabled={noConsent || aiReviewState.loading}
              lang={lang}
              label={AI_LABEL_REVIEW}
            />
          )}
          {mode === 'year' && hasActivities && !isViewOnly && noConsent && (
            <span style={{ ...FONT_TINY, color: TEXT_DISABLED }}>
              {t.aiNoConsent}
            </span>
          )}
          {mode === 'year' && saveState === 'error' && (
            <span style={{
              ...FONT_LABEL,
              background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
              borderRadius: 6, padding: '0.35rem 0.75rem',
            }}>
              {t.saveError}
            </span>
          )}
          {mode === 'multiYear' && (
            <button
              type="button"
              onClick={handleSwitchToYear}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: TEXT_MUTED, textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              ← {t.myBackBtn}
            </button>
          )}
        </div>
      </div>

      {/* ── Multi-year body ───────────────────────────────────────────────── */}
      {mode === 'multiYear' && (
        <MultiYearBody
          programmes={multiYearCache ? multiYearCache.programmes : []}
          ncs={multiYearCache ? multiYearCache.nonconformities : []}
          policy={policy}
          t={t}
          currentYear={currentYear}
          loading={multiYearLoading || multiYearCache === null}
          onRowClick={handleMultiYearRowClick}
        />
      )}

      {/* ── AI review error ───────────────────────────────────────────────── */}
      {mode === 'year' && aiReviewState.error && (
        <div style={{
          ...FONT_SMALL,
          color: ERROR_TEXT, marginBottom: '1rem',
        }}>
          {aiReviewState.error}
        </div>
      )}

      {/* ── AI review actionable panel ────────────────────────────────────── */}
      {mode === 'year' && aiReviewActions && aiReviewActions.length > 0 && (
        <div style={{
          background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10,
          padding: '1rem 1.25rem', marginBottom: '1.5rem',
        }}>
          {/* Panel header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '0.75rem',
          }}>
            <span style={{
              ...FONT_TINY, color: '#7c3aed',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              {t.reviewTitle}
            </span>
            <button
              onClick={() => { setAiReviewActions(null); setCheckedActions([]); }}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#7c3aed',
                padding: 0,
              }}
            >
              {t.dismissSuggestions}
            </button>
          </div>

          {/* Action rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1rem' }}>
            {aiReviewActions.map((action, i) => {
              const isAdd = action.type === 'add';
              const badgeBg   = isAdd ? STATUS_GOOD.bg : STATUS_WARN.bg;
              const badgeText = isAdd ? BRAND_PRIMARY_DARK : STATUS_WARN.text;
              const badgeLabel = isAdd ? t.actionAdd : t.actionModify;
              return (
                <label
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                    background: checkedActions[i] ? '#ede9ff' : BG_SURFACE,
                    border: `1px solid ${checkedActions[i] ? '#c4b5fd' : '#e5e2dc'}`,
                    borderRadius: 8, padding: '0.6rem 0.75rem', cursor: 'pointer',
                    transition: 'background 0.1s, border-color 0.1s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checkedActions[i] || false}
                    onChange={e => {
                      const next = [...checkedActions];
                      next[i] = e.target.checked;
                      setCheckedActions(next);
                    }}
                    style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#7c3aed' }}
                  />
                  <span style={{
                    ...FONT_TINY,
                    background: badgeBg, color: badgeText,
                    borderRadius: 4, padding: '0.1rem 0.4rem',
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}>
                    {badgeLabel}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      ...FONT_BODY_STRONG,
                      color: TEXT_HEADING, marginRight: '0.5rem',
                    }}>
                      {action.activity?.title || '—'}
                    </span>
                    <span style={{
                      ...FONT_LABEL, color: TEXT_MUTED,
                    }}>
                      {action.reason}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Apply / hint */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={handleApplyReview}
              disabled={noneChecked}
              style={{
                ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8,
                padding: '0.45rem 1rem', background: noneChecked ? TEXT_MUTED : BRAND_PRIMARY,
                cursor: noneChecked ? 'not-allowed' : 'pointer',
              }}
            >
              {t.applySelected}
            </button>
            {noneChecked && (
              <span style={{
                ...FONT_SMALL, color: TEXT_DISABLED,
              }}>
                {t.noActionsSelected}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Empty-state panel (no programme for this year) ────────────────── */}
      {mode === 'year' && program === false && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
          padding: '2rem',
        }}>
          {isCreatableYear ? (
            <>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_HEADING, margin: '0 0 0.35rem',
              }}>
                {t.emptyTitle}
              </p>
              <p style={{
                ...FONT_LABEL,
                color: TEXT_DISABLED, margin: '0 0 1.5rem',
              }}>
                {t.emptyHint}
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelSt}>{t.fProgramNotes}</label>
                <textarea
                  value={pendingNotes}
                  onChange={e => setPendingNotes(e.target.value)}
                  rows={3}
                  style={{ ...inputSt, resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={handleAIGenerate}
                  disabled={noConsent || aiGenerateState.loading}
                  style={{
                    ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8,
                    padding: '0.55rem 1.1rem',
                    cursor: (noConsent || aiGenerateState.loading) ? 'not-allowed' : 'pointer',
                    opacity: noConsent ? 0.5 : 1,
                  }}
                >
                  {aiGenerateState.loading ? t.generating : t.btnGenerate}
                </button>
                <button
                  onClick={handleCreateEmpty}
                  disabled={aiGenerateState.loading}
                  style={{
                    ...BTN_SECONDARY, ...FONT_BUTTON, borderRadius: 8,
                    padding: '0.55rem 1.1rem',
                    cursor: aiGenerateState.loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.btnEmpty}
                </button>
                {prevYearExists && (
                  <button
                    onClick={handleCopyPrev}
                    disabled={aiGenerateState.loading || copyingPrev}
                    style={{
                      ...BTN_SECONDARY, ...FONT_BUTTON, borderRadius: 8,
                      padding: '0.55rem 1.1rem',
                      cursor: (aiGenerateState.loading || copyingPrev) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t.btnCopyPrev}
                  </button>
                )}
              </div>

              {noConsent && (
                <p style={{
                  ...FONT_TINY,
                  color: TEXT_DISABLED, margin: '0.75rem 0 0',
                }}>
                  {t.aiNoConsent}
                </p>
              )}
              {aiGenerateState.error && (
                <p style={{
                  ...FONT_SMALL,
                  color: ERROR_TEXT, margin: '0.5rem 0 0',
                }}>
                  {aiGenerateState.error}
                </p>
              )}
            </>
          ) : (
            <p style={{
              ...FONT_LABEL,
              color: TEXT_DISABLED, margin: 0,
            }}>
              {t.noPastProgram}
            </p>
          )}
        </div>
      )}

      {/* ── Programme view ────────────────────────────────────────────────── */}
      {mode === 'year' && !!program && (
        <>
          {/* programNotes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelSt}>{t.fProgramNotes}</label>
            <textarea
              value={program.programNotes || ''}
              onChange={e => {
                setDirty(true);
                setProgram(prev => ({ ...prev, programNotes: e.target.value }));
              }}
              rows={3}
              disabled={isViewOnly}
              style={{ ...inputSt, resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          {/* Summary strip — compliance dashboard */}
          {(() => {
            const ragPair = programmeRag === 'green'
              ? STATUS_GOOD
              : programmeRag === 'amber'
                ? STATUS_WARN
                : programmeRag === 'red'
                  ? STATUS_BAD
                  : null;
            const ragLabel = programmeRag === 'green'
              ? t.ragOnTarget
              : programmeRag === 'amber'
                ? t.ragAtRisk
                : programmeRag === 'red'
                  ? t.ragBelowTarget
                  : null;
            const cardAValueColor = (policy && ragPair) ? ragPair.text : TEXT_HEADING;
            const cardAInlineBadge = (policy && ragPair && ragLabel)
              ? { bg: ragPair.bg, text: ragPair.text, label: ragLabel }
              : null;
            const cardASubline = (policy && avgCoverage != null)
              ? (() => {
                  const gapStr = coverageGap === 0
                    ? '0 pts'
                    : coverageGap > 0
                      ? `+${coverageGap} pts`
                      : `−${Math.abs(coverageGap)} pts`;
                  return (
                    <span>
                      {t.kpiTargetGap
                        .replace('{target}', String(policy.target))
                        .replace('{gap}', gapStr)}
                    </span>
                  );
                })()
              : null;

            const breakdownChip = (key, count, pair, label) => (
              <span
                key={key}
                title={label}
                aria-label={label}
                style={{
                  ...FONT_TINY,
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  background: pair.bg, color: pair.text,
                  borderRadius: 12, padding: '0.15rem 0.5rem',
                }}
              >
                <span style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: pair.text,
                }} />
                {count}
              </span>
            );
            const cardBSubline = policy
              ? (
                <span style={{ display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {breakdownChip('g', ragCounts.green, STATUS_GOOD, t.ragBreakdownGreen)}
                  {breakdownChip('a', ragCounts.amber, STATUS_WARN, t.ragBreakdownAmber)}
                  {breakdownChip('r', ragCounts.red,   STATUS_BAD,  t.ragBelowTarget)}
                </span>
              )
              : null;

            const cardCSubline = (
              <span>
                {t.kpiCompletedSubline
                  .replace('{planned}', String(plannedCount))
                  .replace('{cancelled}', String(cancelledCount))}
              </span>
            );

            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <KpiCard
                  label={t.kpiCoverageYtd}
                  value={avgCoverage != null ? `${avgCoverage}%` : '—'}
                  valueColor={cardAValueColor}
                  inlineBadge={cardAInlineBadge}
                  subline={cardASubline}
                />
                <KpiCard
                  label={t.cntActivities}
                  value={totalActivities}
                  valueColor={TEXT_HEADING}
                  inlineBadge={null}
                  subline={cardBSubline}
                />
                <KpiCard
                  label={t.kpiStatus}
                  value={completedCount}
                  valueColor={BRAND_PRIMARY_DARK}
                  inlineBadge={null}
                  subline={cardCSubline}
                />
              </div>
            );
          })()}

          {/* Annual timeline (read-only visualisation) */}
          {(() => {
            const monthLabelsEs = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
            const monthLabelsEn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const monthLabels = lang === 'es' ? monthLabelsEs : monthLabelsEn;
            const isLeap = (y) => ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0);
            const dayOfYear = (dateStr) => {
              const d = new Date(`${dateStr}T00:00:00`);
              if (isNaN(d)) return null;
              const start = new Date(d.getFullYear(), 0, 1);
              return Math.floor((d - start) / 86400000) + 1;
            };
            const posPercent = (dateStr) => {
              const d = dayOfYear(dateStr);
              if (d == null) return null;
              const dy = isLeap(new Date(`${dateStr}T00:00:00`).getFullYear()) ? 366 : 365;
              const raw = (d - 1) / (dy - 1) * 100;
              return Math.max(0, Math.min(100, raw));
            };
            const sameCalDay = (a, b) => {
              if (!a || !b) return false;
              return a.slice(0, 10) === b.slice(0, 10);
            };
            const LABEL_COL_PX = 180;
            const labelColTemplate = `${LABEL_COL_PX}px 1fr`;
            const TRACK_H = 16;
            const MARK_H = 13;
            const MARK_TOP = (TRACK_H - MARK_H) / 2;
            const fillColorFor = (act) => {
              const band = (policy && act.coveragePercent != null) ? coverageRag(act.coveragePercent, policy) : null;
              if (band === 'green') return STATUS_GOOD.text;
              if (band === 'amber') return STATUS_WARN.text;
              if (band === 'red')   return STATUS_BAD.text;
              return BRAND_PRIMARY;
            };
            const renderMarker = (key, leftPercent, style) => (
              <div key={key} style={{
                position: 'absolute',
                top: `${MARK_TOP}px`,
                left: `calc(${leftPercent}% - ${MARK_H / 2}px)`,
                width: `${MARK_H}px`, height: `${MARK_H}px`, borderRadius: '50%',
                ...style,
              }} />
            );

            return (
              <div>
                <button
                  type="button"
                  onClick={() => setTimelineExpanded(v => !v)}
                  style={{
                    ...FONT_LABEL,
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
                    borderRadius: 10, padding: '0.6rem 0.85rem',
                    marginBottom: timelineExpanded ? '0' : '1.5rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    color: TEXT_HEADING,
                    borderBottomLeftRadius: timelineExpanded ? 0 : 10,
                    borderBottomRightRadius: timelineExpanded ? 0 : 10,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: TEXT_MUTED }}>{timelineExpanded ? '⌄' : '›'}</span>
                    {t.timelineHeader}
                  </span>
                  <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>
                    {t.timelineActivityCount.replace('{count}', String(timelineActivities.length))}
                  </span>
                </button>

                {timelineExpanded && (
                  <div style={{
                    background: BG_SUBTLE,
                    borderLeft: `1px solid ${BORDER_DEFAULT}`,
                    borderRight: `1px solid ${BORDER_DEFAULT}`,
                    borderBottom: `1px solid ${BORDER_DEFAULT}`,
                    borderRadius: '0 0 10px 10px',
                    padding: '0.85rem 1rem',
                    marginBottom: '1.5rem',
                  }}>
                    {/* Month axis */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: labelColTemplate,
                      paddingBottom: '0.4rem',
                    }}>
                      <div />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                        {monthLabels.map((m, i) => (
                          <div key={i} style={{ ...FONT_TINY, color: TEXT_MUTED, textAlign: 'center' }}>
                            {m}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Activity rows */}
                    {timelineActivities.map((act, idx) => {
                      const isPlanned   = act.status === 'Planned';
                      const isCompleted = act.status === 'Completed';
                      const isCancelled = act.status === 'Cancelled';
                      const plannedPct  = act.plannedDate ? posPercent(act.plannedDate) : null;
                      const actualPct   = act.actualDate  ? posPercent(act.actualDate)  : null;
                      const chipLabel = isPlanned
                        ? (t.statusLabels?.Planned || 'Planned')
                        : isCancelled
                          ? (t.statusLabels?.Cancelled || 'Cancelled')
                          : null;

                      let markers = null;
                      if (isCancelled) {
                        const pct = plannedPct != null ? plannedPct : actualPct;
                        if (pct != null) {
                          markers = renderMarker('c', pct, {
                            background: BG_SHELL,
                            border: `1.5px dashed ${TEXT_DISABLED}`,
                          });
                        }
                      } else if (isPlanned) {
                        if (plannedPct != null) {
                          markers = renderMarker('p', plannedPct, {
                            background: SURFACE_WHITE,
                            border: `1.5px solid ${TEXT_DISABLED}`,
                          });
                        }
                      } else if (isCompleted) {
                        const sameDay = sameCalDay(act.plannedDate, act.actualDate);
                        const hasPlanned = !!act.plannedDate;
                        if (!hasPlanned || sameDay) {
                          if (actualPct != null) {
                            markers = renderMarker('a', actualPct, {
                              background: fillColorFor(act),
                              border: 'none',
                            });
                          }
                        } else {
                          const pieces = [];
                          if (plannedPct != null && actualPct != null) {
                            const leftPct  = Math.min(plannedPct, actualPct);
                            const rightPct = Math.max(plannedPct, actualPct);
                            pieces.push(
                              <div key="line" style={{
                                position: 'absolute',
                                top: `${TRACK_H / 2 - 0.75}px`,
                                left: `calc(${leftPct}% + ${MARK_H / 2 - 6.5}px)`,
                                width: `calc(${rightPct - leftPct}% - 0px)`,
                                height: '1.5px',
                                background: TEXT_DISABLED,
                              }} />
                            );
                          }
                          if (plannedPct != null) {
                            pieces.push(renderMarker('p', plannedPct, {
                              background: SURFACE_WHITE,
                              border: `1.5px solid ${TEXT_DISABLED}`,
                            }));
                          }
                          if (actualPct != null) {
                            pieces.push(renderMarker('a', actualPct, {
                              background: fillColorFor(act),
                              border: 'none',
                            }));
                          }
                          markers = pieces;
                        }
                      }

                      return (
                        <div key={act.id} style={{
                          display: 'grid',
                          gridTemplateColumns: labelColTemplate,
                          alignItems: 'center',
                          gap: '0.6rem',
                          padding: '0.35rem 0',
                          borderTop: idx === 0 ? 'none' : `0.5px solid ${BORDER_DEFAULT}`,
                        }}>
                          <button
                            type="button"
                            onClick={() => handleTimelineRowClick(act.id)}
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex', alignItems: 'center', gap: '0.4rem',
                              minWidth: 0,
                            }}
                          >
                            <span style={{
                              ...FONT_SMALL,
                              color: act.title ? TEXT_HEADING : TEXT_DISABLED,
                              fontStyle: act.title ? 'normal' : 'italic',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              flex: 1, minWidth: 0,
                            }}>
                              {act.title || t.unnamedActivity}
                            </span>
                            {chipLabel && (
                              <span style={{
                                ...FONT_TINY,
                                background: BG_MUTED, color: TEXT_DISABLED,
                                borderRadius: 8, padding: '0.05rem 0.4rem',
                                flexShrink: 0,
                              }}>
                                {chipLabel}
                              </span>
                            )}
                          </button>
                          <div style={{
                            position: 'relative',
                            height: `${TRACK_H}px`,
                            background: SURFACE_WHITE,
                            border: `0.5px solid ${BORDER_DEFAULT}`,
                            borderRadius: 8,
                          }}>
                            {markers}
                          </div>
                        </div>
                      );
                    })}

                    {/* Legend */}
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: '1rem',
                      paddingTop: '0.7rem', marginTop: '0.5rem',
                      borderTop: `0.5px dashed ${BORDER_DEFAULT}`,
                    }}>
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: SURFACE_WHITE, border: `1.5px solid ${TEXT_DISABLED}` }} />
                        {t.legendPlanned}
                      </span>
                      {policy && (
                        <>
                          <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: STATUS_GOOD.text }} />
                            {t.legendOnTarget}
                          </span>
                          <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: STATUS_WARN.text }} />
                            {t.legendAtRisk}
                          </span>
                          <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: STATUS_BAD.text }} />
                            {t.legendBelowTarget}
                          </span>
                        </>
                      )}
                      {!policy && (
                        <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: BRAND_PRIMARY }} />
                          {t.legendCompleted}
                        </span>
                      )}
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: BG_SHELL, border: `1.5px dashed ${TEXT_DISABLED}` }} />
                        {t.legendCancelled}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Activity cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {activities.map((act) => {
              const isExpanded = expandedActivity === act.id;
              const sc         = ACTIVITY_STATUS_COLORS[act.status] || ACTIVITY_STATUS_COLORS.Planned;
              return (
                <div key={act.id} id={`awareness-activity-${act.id}`} style={{
                  background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 10, overflow: 'hidden',
                }}>
                  {/* Card header (collapsed + expanded) */}
                  <div
                    onClick={() => handleToggleActivity(act.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.85rem 1rem', cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: 1, minWidth: 0 }}>
                      <span style={{
                        ...FONT_LABEL,
                        color: TEXT_MUTED, flexShrink: 0,
                      }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{
                        ...FONT_BODY_STRONG,
                        color: act.title ? TEXT_HEADING : TEXT_DISABLED,
                        fontStyle: act.title ? 'normal' : 'italic',
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {act.title || t.unnamedActivity}
                      </span>
                      {!isExpanded && (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexShrink: 0 }}>
                          <span style={{
                            ...FONT_TINY,
                            background: BG_SUBTLE, color: TEXT_MUTED,
                            borderRadius: 4, padding: '0.1rem 0.4rem',
                          }}>
                            {t.formatLabels[act.format] || act.format}
                          </span>
                          {act.plannedDate && (
                            <span style={{
                              ...FONT_TINY, color: TEXT_MUTED,
                            }}>
                              {act.plannedDate}
                            </span>
                          )}
                          <span style={{
                            ...FONT_TINY,
                            background: sc.bg, color: sc.text,
                            borderRadius: 4, padding: '0.1rem 0.4rem',
                          }}>
                            {t.statusLabels[act.status] || act.status}
                          </span>
                          <span style={{
                            ...FONT_TINY,
                            color: act.coveragePercent != null ? BRAND_PRIMARY_DARK : TEXT_DISABLED,
                          }}>
                            {act.coveragePercent != null ? `${act.coveragePercent}%` : '—'}
                          </span>
                          {(() => {
                            if (!policy || act.coveragePercent == null) return null;
                            const band = coverageRag(act.coveragePercent, policy);
                            const pair = band === 'green' ? STATUS_GOOD
                              : band === 'amber' ? STATUS_WARN
                              : STATUS_BAD;
                            const label = band === 'green' ? t.ragOnTarget
                              : band === 'amber' ? t.ragAtRisk
                              : t.ragBelowTarget;
                            return (
                              <span style={{
                                ...FONT_TINY,
                                background: pair.bg, color: pair.text,
                                borderRadius: 10, padding: '0.1rem 0.45rem', fontWeight: 500,
                              }}>
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded form */}
                  {isExpanded && (
                    <div style={{ borderTop: `1px solid ${BG_SHELL}`, padding: '1rem 1rem 1.25rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

                        {/* Row 1: title */}
                        <div style={fieldSt}>
                          <label style={labelSt}>{t.colTitle}</label>
                          <input
                            value={act.title}
                            onChange={e => setActivityField(act.id, 'title', e.target.value)}
                            placeholder={t.titlePlaceholder}
                            disabled={isViewOnly}
                            style={cellInputSt}
                          />
                        </div>

                        {/* Row 2: topic | format | language */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colTopic}</label>
                            <input
                              value={act.topic}
                              onChange={e => setActivityField(act.id, 'topic', e.target.value)}
                              placeholder={t.topicPlaceholder}
                              disabled={isViewOnly}
                              style={cellInputSt}
                            />
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colFormat}</label>
                            <select
                              value={act.format}
                              onChange={e => setActivityField(act.id, 'format', e.target.value)}
                              disabled={isViewOnly}
                              style={{ ...cellInputSt, cursor: isViewOnly ? 'default' : 'pointer' }}
                            >
                              {FORMATS.map(f => (
                                <option key={f} value={f}>{t.formatLabels[f]}</option>
                              ))}
                            </select>
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colLanguage}</label>
                            <select
                              value={act.language}
                              onChange={e => setActivityField(act.id, 'language', e.target.value)}
                              disabled={isViewOnly}
                              style={{ ...cellInputSt, cursor: isViewOnly ? 'default' : 'pointer' }}
                            >
                              {LANGUAGES.map(l => (
                                <option key={l} value={l}>{t.languageLabels[l]}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Row 3: targetAudience | plannedDate | actualDate */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colAudience}</label>
                            <input
                              value={act.targetAudience}
                              onChange={e => setActivityField(act.id, 'targetAudience', e.target.value)}
                              placeholder={t.audiencePlaceholder}
                              disabled={isViewOnly}
                              style={cellInputSt}
                            />
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colPlannedDate}</label>
                            <input
                              type="date"
                              value={act.plannedDate || ''}
                              onChange={e => setActivityField(act.id, 'plannedDate', e.target.value)}
                              disabled={isViewOnly}
                              style={cellInputSt}
                            />
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colActualDate}</label>
                            <input
                              type="date"
                              value={act.actualDate || ''}
                              onChange={e => setActivityField(act.id, 'actualDate', e.target.value || null)}
                              disabled={isViewOnly}
                              style={cellInputSt}
                            />
                          </div>
                        </div>

                        {/* Row 4: status | totalStaff | attendees | coveragePercent */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colStatus}</label>
                            <select
                              value={act.status}
                              onChange={e => setActivityField(act.id, 'status', e.target.value)}
                              disabled={isViewOnly}
                              style={{
                                ...cellInputSt, cursor: isViewOnly ? 'default' : 'pointer',
                                background: sc.bg, color: sc.text, fontWeight: 600,
                              }}
                            >
                              {STATUSES.map(s => (
                                <option key={s} value={s}>{t.statusLabels[s]}</option>
                              ))}
                            </select>
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colTotalStaff}</label>
                            <input
                              type="number"
                              min="0"
                              value={act.totalStaff}
                              onChange={e => {
                                const v = e.target.value === '' ? 0 : Number(e.target.value);
                                setActivityField(act.id, 'totalStaff', v);
                              }}
                              disabled={isViewOnly}
                              style={{ ...cellInputSt, textAlign: 'right' }}
                            />
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colAttendees}</label>
                            <input
                              type="number"
                              min="0"
                              value={act.attendees ?? ''}
                              onChange={e => {
                                const v = e.target.value === '' ? null : Number(e.target.value);
                                setActivityField(act.id, 'attendees', v);
                              }}
                              disabled={isViewOnly}
                              style={{ ...cellInputSt, textAlign: 'right' }}
                            />
                          </div>
                          <div style={fieldSt}>
                            <label style={labelSt}>{t.colCoverage}</label>
                            <div style={{
                              ...cellInputSt,
                              background: BG_SUBTLE, color: act.coveragePercent != null ? BRAND_PRIMARY_DARK : TEXT_DISABLED,
                              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                            }}>
                              {act.coveragePercent != null ? `${act.coveragePercent}%` : '—'}
                            </div>
                          </div>
                        </div>

                        {/* Row 5: evidenceId (full width) */}
                        <div style={fieldSt}>
                          <label style={labelSt}>{t.colEvidenceId}</label>
                          <EvidenceSelector
                            orgId={org.id}
                            lang={lang}
                            value={act.evidenceId || null}
                            onChange={(id) => setActivityField(act.id, 'evidenceId', id || null)}
                            multiple={false}
                            density="compact"
                            disabled={isViewOnly}
                          />
                        </div>

                        {/* Row 6: notes (full width) */}
                        <div style={fieldSt}>
                          <label style={labelSt}>{t.colNotes}</label>
                          <textarea
                            rows={3} value={act.notes || ''}
                            onChange={e => setActivityField(act.id, 'notes', e.target.value)}
                            placeholder={t.notesPlaceholder}
                            disabled={isViewOnly}
                            style={{ ...cellInputSt, resize: 'vertical', minHeight: '4.5rem' }}
                          />
                        </div>

                        {/* Row 7: linkedObjective */}
                        <div style={fieldSt}>
                          <label style={labelSt}>{t.fLinkedObjective}</label>
                          <select
                            value={act.linkedObjectiveId || ''}
                            onChange={e => setActivityField(act.id, 'linkedObjectiveId', e.target.value || null)}
                            disabled={isViewOnly}
                            style={{ ...cellInputSt, cursor: isViewOnly ? 'default' : 'pointer' }}
                          >
                            <option value="">{t.linkedObjectiveNone}</option>
                            {objectives.map(obj => (
                              <option key={obj.id} value={obj.id}>
                                {obj.ref} — {obj.objective}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Open nonconformity (red-band only) */}
                        {policy
                          && act.coveragePercent != null
                          && act.status === 'Completed'
                          && coverageRag(act.coveragePercent, policy) === 'red'
                          && !isViewOnly
                          && typeof onNavigate === 'function' && (
                          <div style={fieldSt}>
                            <button
                              type="button"
                              onClick={() => {
                                const titleStr = act.title || t.unnamedActivity;
                                const description = t.ncDescriptionTemplate
                                  .replace('{title}', titleStr)
                                  .replace('{coverage}', String(act.coveragePercent))
                                  .replace('{target}', String(policy.target))
                                  .replace('{year}', String(year));
                                onNavigate('nonconformities', {
                                  prefill: { source: NC_SOURCE_AWARENESS_GAP, description },
                                });
                              }}
                              style={{
                                ...FONT_LABEL,
                                alignSelf: 'flex-start',
                                padding: '0.45rem 0.85rem',
                                borderRadius: 6,
                                border: `1px solid ${ERROR_BORDER}`,
                                background: ERROR_BG,
                                color: ERROR_TEXT,
                                cursor: 'pointer',
                              }}
                            >
                              {t.openNcBtn}
                            </button>
                          </div>
                        )}

                        {/* Delete button */}
                        {!isViewOnly && (
                          <div style={{ borderTop: `1px solid ${BG_SHELL}`, paddingTop: '0.75rem' }}>
                            <button
                              onClick={() => handleDeleteActivity(act.id)}
                              style={{
                                ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 6,
                                padding: '0.3rem 0.75rem', cursor: 'pointer',
                              }}
                            >
                              {t.deleteBtn}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add activity */}
          {!isViewOnly && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                onClick={handleAddActivity}
                style={{
                  ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.85rem',
                  cursor: 'pointer',
                }}
              >
                {t.addActivity}
              </button>
            </div>
          )}
        </>
      )}

      {/* Sticky action bar */}
      {mode === 'year' && !!program && !isViewOnly && (
        <div style={{
          position: 'sticky', bottom: 0,
          marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
          marginTop: '1.5rem',
          padding: '0.85rem 1.5rem', background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
            {dirty && (
              <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
                {t.unsavedLabel}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8, background: saveBg,
                padding: '0.55rem 1.1rem', cursor: isSaving ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {saveLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
